<template>
  <div>
    <!-- Hero Section -->
    <div class="w-full bg-primary text-white text-center tw-flex justify-center flex-col px-4 py-6 sm:py-8 md:py-10 min-h-[100px] md:min-h-[130px]">
      <h1 class="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-1 sm:mb-2">{{ onboardingTextConfig.personalHomeTitle }}</h1>
      <p class="text-xs sm:text-sm md:text-base opacity-90 leading-snug">{{ onboardingTextConfig.personalHomeSubtitle }}</p>
    </div>

    <!-- Main Content -->
    <div class="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      <PasswordAndSecurity v-if="isPasswordAndSecurity"/>

      <div v-else class="grid gap-6 lg:grid-cols-[1fr_320px] items-start">

        <!-- ═══════════════ Main Content Column ═══════════════ -->
        <div class="space-y-6 min-w-0">

          <!-- ── Profile Card ── -->
          <div class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div class="p-4 sm:p-6">
              <div class="tw-flex flex-col items-center gap-4 sm:flex-row sm:items-start sm:gap-5 lg:gap-6">

                <!-- Left: Avatar with camera upload -->
                <div class="tw-flex flex-col items-center gap-3 shrink-0">
                  <div class="relative">
                    <AvatarNew :user="profile" class="rounded-full border-4 border-gray-100 shadow-lg" size="large"/>
                    <button
                      type="button"
                      class="absolute bottom-0 right-0 tw-flex items-center justify-center w-7 h-7 rounded-full bg-primary border-2 border-white shadow-md hover:bg-primary/80 transition-colors"
                      @click="triggerPhotoUpload"
                      v-tooltip="$t('personal_home_update_profile_picture')"
                    >
                      <svg v-if="!uploadingPhoto" class="h-3.5 w-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
                      </svg>
                      <svg v-else class="h-3.5 w-3.5 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                      </svg>
                    </button>
                  </div>
                  <input
                    ref="photoInput"
                    type="file"
                    accept="image/*"
                    class="hidden"
                    @change="onPhotoSelected"
                  />
                </div>

                <!-- Center: Name + bio + social -->
                <div class="flex-1 min-w-0 text-center sm:text-left">
                  <h2 class="text-xl font-bold text-gray-900 leading-tight">{{ profile.name }}</h2>
                  <p v-if="profile?.profile_translations[locale]?.bio" :class="['mt-1 text-sm text-gray-600 leading-relaxed', bioExpanded ? '' : 'line-clamp-2']">
                    {{ profile.profile_translations[locale].bio }}
                  </p>
                  <p v-else @click="showUpdateProfileModal" class="underline text-bold text-primary hover:underline cursor-pointer">
                    {{ $t('personal_home_no_bio_yet') }}
                  </p>
                  <button
                    v-if="profile.bio && profile.bio.length > 120"
                    class="mt-0.5 text-xs text-primary hover:underline"
                    @click="toggleBioExpanded"
                  >{{ bioExpanded ? $t('personal_home_show_less') : $t('personal_home_read_more') }}
                  </button>
                  <div v-if="userBaseInfoData" class="mt-1 tw-flex items-center justify-center sm:justify-start">
                    <UserSocialButtons :user="userBaseInfoData"/>
                  </div>
                </div>

                <!-- Right: Actions -->
                <div class="tw-flex flex-col gap-3 shrink-0 items-stretch w-full sm:w-auto sm:min-w-[155px] lg:min-w-[170px]">
                  <!-- Homepage toggle -->
                  <div class="tw-flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 cursor-pointer"
                       :class="{'bg-primary-lightest': userPreferences.usePersonalHome, 'bg-primary': !userPreferences.usePersonalHome}"
                       @click.stop="togglePersonalHome"
                       v-tooltip="$t('personal_home_homepage_tooltip')">
                    <svg class="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                         :class="{'text-primary': userPreferences.usePersonalHome, 'text-white': !userPreferences.usePersonalHome}">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
                    </svg>
                    <label class="cursor-pointer text-xs text-primary flex-1"
                           :class="{'text-primary': userPreferences.usePersonalHome, 'text-white': !userPreferences.usePersonalHome}">{{
                        $t('personal_home_make_homepage')
                      }}</label>
                    <ToggleButton :enabled="userPreferences.usePersonalHome" size="small"/>
                  </div>
                </div>

              </div>

              <div class="tw-flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-t border-gray-100 pt-3 sm:pt-4 mt-3 sm:mt-4">
                <!-- Actor + Product claims -->
                <div class="tw-flex flex-col gap-2 min-w-0">
                  <!-- Actor claims -->
                  <div class="tw-flex items-center gap-1.5 flex-wrap min-w-0">
                    <span class="text-xs font-medium text-gray-400 shrink-0">{{ $t('profile_settings_my_actors', { actors: actorsLabel }) }}:</span>
                    <router-link
                      v-for="actor in actorClaims.slice(0, 3)"
                      :key="actor.id"
                      :to="'/actors/' + actor.id"
                      class="ph-claim-chip"
                    >{{ actor.name }}
                    </router-link>
                    <button
                      v-if="actorClaims.length > 3"
                      class="ph-claim-chip ph-claim-chip--more"
                      @click="scrollToSection('my-actors-section')"
                    >+{{ actorClaims.length - 3 }} {{ $t('actor_detail_show_all') }}
                    </button>

                  </div>
                  <!-- Product claims -->
                  <div v-if="productClaims.length || showAddProductButton" class="tw-flex items-center gap-1.5 flex-wrap min-w-0">
                    <span class="text-xs font-medium text-gray-400 shrink-0">{{ $t('profile_settings_my_products', { products: productsLabel }) }}:</span>
                    <a
                      v-for="product in productClaims.slice(0, 3)"
                      :key="product.id"
                      :href="'/actors/' + product.id"
                      class="ph-claim-chip"
                    >{{ product.name }}</a>
                    <button
                      v-if="productClaims.length > 3"
                      class="ph-claim-chip ph-claim-chip--more"
                      @click="scrollToSection('my-products-section')"
                    >+{{ productClaims.length - 3 }} {{ $t('actor_detail_show_all') }}
                    </button>

                  </div>
                </div>
                <!-- Action buttons -->
                <div class="tw-flex items-center gap-1 shrink-0">
                  <DsButton
                    size="small"
                    variant="rounded-secondary"
                    icon="bell"
                    to="/notifications?o=true"
                    v-tooltip.top="$t('personal_home_notifications_settings_tooltip')"
                  />
                  <DsButton
                    size="small"
                    variant="rounded-secondary"
                    icon="lock"
                    to="#password-and-security"
                    v-tooltip.top="$t('personal_home_change_contact_tooltip')"
                  />
                  <DsButton
                    variant="rounded"
                    @click="showUpdateProfileModal"
                    size="small"
                    :label="$t('profile_settings_update')"
                    icon="pencil-icon"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- ── Quick Actions ── -->
          <div class="tw-flex flex-wrap gap-2">
            <DsButton
              variant="rounded"
              v-if="showAddActorButton"
              :label="$t('add_actor_add_button', { actor: actorLabel })"
              v-tooltip="$t('profile_settings_add_actor', { actor: actorLabel })"
              @click="showCreateActorModal"
              size="small"
              icon="plus"
            />
            <DsButton
              variant="rounded-secondary"
              v-if="showAddProductButton"
              :label="$t('add_product_add_button', { product: productLabel })"
              v-tooltip="isProductButtonDisabled ? $t('add_product_add_button_disabled', { actor: actorLabel, product: productLabel }) : $t('add_product_add_button', { product: productLabel })"
              @click="showAddProductModal"
              :disabled="isProductButtonDisabled"
              size="small"
              icon="plus"
            />
            <DsButton
              v-if="$store.getters.canCreateAnnouncement"
              size="small"
              variant="rounded-minimal"
              @click="showManageAnnouncementModal"
              :label="$t('actor_add_announcement', { announcement: this.announcementLabel })"
              icon="plus"
            />
            <DsButton
              v-if="$store.getters.canCreateChallenge"
              size="small"
              variant="rounded-minimal"
              @click="showAddChallengeModal"
              :label="$t('add_challenge_panel_title', { challenge: challengeLabel })"
              icon="plus"
            />
          </div>

          <!-- ── Analytics Cards ── -->
          <div class="grid gap-3 sm:gap-4 grid-cols-2 md:grid-cols-4" :class="{ 'animate-pulse': !stats }">
            <div
              v-for="stat in analyticsStats"
              :key="stat.key"
              class="bg-white rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4 overflow-hidden transition-shadow hover:shadow-md"
            >
              <div class="tw-flex items-start justify-between">
                <div class="rounded-lg bg-primary-extra-lightest p-1.5 sm:p-2">
                  <svg class="h-4 w-4 sm:h-5 sm:w-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="stat.iconPath"/>
                  </svg>
                </div>
                <div>
                  <Icon name="information" size="14" fill="gray" v-tooltip.top="$t('personal_home_stat_last_30_days')"/>
                </div>
              </div>
              <div class="mt-2 sm:mt-3">
                <template v-if="stats">
                  <p class="text-xl sm:text-2xl font-bold text-gray-900 leading-none">{{ stat.value }}</p>
                  <p class="mt-1 text-xs sm:text-sm font-medium text-gray-700 leading-snug">{{ stat.label }}</p>
                  <div class="mt-1 sm:mt-1.5 tw-flex items-center gap-1 sm:gap-1.5 flex-wrap">
                    <div
                      v-if="stat.change"
                      :class="['tw-flex items-center gap-0.5 text-xs font-medium', stat.trend === 'up' ? 'text-green-600' : 'text-red-500']"
                    >
                      <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5"
                              :d="stat.trend === 'up' ? 'M5 10l7-7m0 0l7 7m-7-7v18' : 'M19 14l-7 7m0 0l-7-7m7 7V3'"/>
                      </svg>
                      {{ stat.change }}
                    </div>
                    <span class="text-xs text-gray-400 leading-snug">{{ stat.change ? $t('personal_home_vs_prev_period') : stat.description }}</span>
                  </div>
                </template>
                <template v-else>
                  <div class="h-6 w-12 bg-gray-200 rounded mb-2"/>
                  <div class="h-3 w-3/4 bg-gray-200 rounded"/>
                  <div class="mt-2 h-2.5 w-1/2 bg-gray-200 rounded"/>
                </template>
              </div>
            </div>
          </div>

          <!-- ── Feed: Announcements · Members · Challenges ── -->
          <PersonalHomeContent/>

          <!-- ── Mobile sidebar widgets (hidden on lg where sidebar is visible) ── -->
          <div class="lg:hidden space-y-4">
            <TractionTips
              @open-update-profile-modal="showUpdateProfileModal"
            />
          </div>

        </div><!-- /Main Column -->

        <!-- ═══════════════ Right Sidebar ═══════════════ -->
        <div class="hidden lg:block w-[320px] min-w-[320px] max-w-[320px] overflow-hidden">
          <div class="space-y-4">

            <!-- Traction Tips -->
            <TractionTips
              @open-update-profile-modal="showUpdateProfileModal"
            />

            <!-- My Actors quick -->
            <!--            <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                          <h4 class="text-sm font-semibold text-gray-900 mb-3">{{ $t('profile_settings_my_actors', { actors: actorsLabel }) }}</h4>
                          <p class="text-xs text-gray-500">
                            {{ actorClaims.length ? actorClaims.length + ' actor' + (actorClaims.length !== 1 ? 's' : '') + ' connected' : $t('profile_no_actors_found') }}
                          </p>
                          <button
                            v-if="showAddActorButton"
                            class="mt-3 w-full tw-flex items-center justify-center gap-1.5 text-xs font-medium border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50 text-gray-600 transition-colors"
                            @click="showCreateActorModal"
                          >
                            <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                            </svg>
                            Add an Actor
                          </button>
                        </div>-->

            <!-- My Knowledge -->
            <!--            <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-4" v-if="$store.getters.hasAccessToKnowledgeBase">
                          <h4 class="text-sm font-semibold text-gray-900 mb-3">My Knowledge</h4>
                          <div v-if="sidebarKnowledgePosts.length" class="space-y-2">
                            <router-link
                              v-for="post in sidebarKnowledgePosts.slice(0, 3)"
                              :key="post.id"
                              :to="post.community_id ? `/communities/${post.community_id}/knowledge-base/posts/${post.id}` : `/knowledge-base/posts/${post.id}`"
                              class="block text-xs text-primary hover:underline truncate"
                            >
                              {{ post.title }}
                            </router-link>
                          </div>
                          <p v-else class="text-xs text-gray-500">
                            {{ $t('profile_no_knowledge_base_posts_found') }}
                          </p>
                          <router-link
                            to="/dashboards/knowledge-base"
                            class="mt-3 w-full tw-flex items-center justify-center text-xs font-medium border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50 text-gray-600 transition-colors"
                          >
                            Browse Knowledge Base
                          </router-link>
                        </div>-->

            <!-- My Communities sidebar -->
            <div
              class="bg-white rounded-xl border border-gray-200 shadow-sm p-4"
              v-if="$store.getters.hasAccessToCommunities && joinedCommunities.length"
            >
              <h4 class="text-sm font-semibold text-gray-900 mb-3">{{ $t('personal_home_my_communities') }}</h4>
              <div class="space-y-2">
                <PersonalHomeCommunityCard
                  v-for="community in joinedCommunities.slice(0, 3)"
                  :key="community.id"
                  :community="community"
                  @click="viewCommunity(community)"
                />
              </div>
              <button
                v-if="joinedCommunities.length > 3"
                class="mt-3 text-xs text-primary hover:underline"
                @click="showAllSidebarCommunities = !showAllSidebarCommunities"
              >
                {{ showAllSidebarCommunities ? $t('actor_detail_show_less') : $t('actor_detail_show_all') }}
              </button>
            </div>

            <!-- My Creative Regions (Spotting Areas) sidebar -->
            <!--            <div
                          class="bg-white rounded-xl border border-gray-200 shadow-sm p-4"
                          v-if="$store.getters.hasAccessToNewSpottingAreas && joinedSpottingAreas.length"
                        >
                          <h4 class="text-sm font-semibold text-gray-900 mb-3">
                            {{ $t('profile_settings_my_spotting_areas', { spottingareas: spottingAreasLabel }) }}
                          </h4>
                          <div class="space-y-2">
                            <div
                              v-for="area in joinedSpottingAreas.slice(0, 5)"
                              :key="area.id"
                              class="tw-flex items-center justify-between text-sm gap-2"
                            >
                              <a
                                class="tw-flex items-center gap-1 text-primary text-xs hover:underline cursor-pointer truncate flex-1"
                                @click="viewSpottingArea(area)"
                              >
                                {{ area.name }}
                                <svg class="h-3 w-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                                </svg>
                              </a>
                              <label class="tw-flex items-center gap-1 text-xs text-gray-500 cursor-pointer whitespace-nowrap shrink-0">
                                <input
                                  type="checkbox"
                                  :checked="userPreferences.preferredSpottingAreaId === area.id"
                                  @change="toggleSidebarSpottingAreaPreference(area.id)"
                                  class="w-3 h-3 text-primary border-gray-300 rounded focus:ring-primary"
                                />
                                {{ userPreferences.preferredSpottingAreaId === area.id ? $t('profile_preferred') : $t('profile_set_as_preferred') }}
                              </label>
                            </div>
                          </div>
                        </div>-->

          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
  import OnboardingMixin from '../../util/OnboardingMixin.js'
  import { ACTION_TYPES as NOTIFICATION_ACTION_TYPES } from '../../store/modules/notifications.js'
  import Avatar from '../Avatar/Avatar.vue'
  import TranslationsMixin from '../../util/TranslationsMixin.js'
  import UiMixin from '../../util/UiMixin.js'
  import IntroJsMixin from '../../util/IntroJsMixin.js'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import { MUTATION_TYPES as FILTERS_MUTATION_TYPES } from '@/store/modules/filters'
  import { ACTION_TYPES as COMMUNITY_ACTION_TYPES } from '@/store/modules/communities.js'
  import PersonalHomeContent from '@/components/Profile/PersonalHomeContent.vue'
  import PersonalHomeCommunityCard from '@/components/Profile/PersonalHomeCommunityCard.vue'
  import PasswordAndSecurity from '@/components/Profile/PasswordAndSecurity.vue'
  import TractionTips from '@/components/Profile/TractionTips.vue'
  import UserSocialButtons from '@/Domain/User/Components/UserSocialButtons.vue'
  import { transformLoggedInUserToUserBaseInfoData } from '@/Domain/User/Api/UserApiTransformers'
  import MODAL_IDS from '@/constants/modal-ids.js'
  import DsButton from '@/components/DsButton/DsButton.vue'
  import { trackHeapEvent, trackMatomoEvent } from '@/util/analytics.js'
  import { MATOMO_EVENT_ACTIONS, MATOMO_EVENT_CATEGORIES } from '@/constants/analytics-constants'
  import CommunityMixin from '@/util/CommunityMixin'
  import SpottingAreaMixin from '@/util/SpottingAreaMixin'
  import { fetchProfile, getUserPreferences, getUserStats, updateProfile, updateUserPreferences } from '@/api/user'
  import { leaveCommunity } from '@/api/communities.js'
  import { fetchPostsByUser } from '@/api/knowledge-base.js'
  import AvatarNew from '@/components/Avatar/AvatarNew.vue'
  import ToggleButton from '@/components/Form/ToggleButton.vue'
  import Icon from '@/components/Icon/Icon.vue'
  import { getImageForCommunity } from '@/util/helpers'
  import { updateActor } from '@/api/actors'

  export default {
    data () {
      return {
        bioExpanded: false,
        uploadingPhoto: false,
        userPreferences: {
          preferredCommunityId: null,
          preferredSpottingAreaId: null,
          usePersonalHome: false,
        },
        stats: null,
        sidebarKnowledgePosts: [],
        showAllSidebarCommunities: false,
      }
    },
    computed: {
      profile () {
        return this.$store.state.user.profile
      },
      isPasswordAndSecurity () {
        return this.$route.hash === '#password-and-security'
      },
      userBaseInfoData () {
        if (!this.profile?.id) return null
        return transformLoggedInUserToUserBaseInfoData(this.profile)
      },
      actorClaims () {
        if (!this.profile?.claims) return []
        return this.profile.claims.filter(c => c.actor_type !== 'Product')
      },
      productClaims () {
        if (!this.profile?.claims) return []
        return this.profile.claims.filter(c => c.actor_type === 'Product')
      },
      showAddActorButton () {
        if (this.$store.getters.isMember) return true
        return this.$store.getters.userCanCreate &&
          this.$store.getters.claimableActorTypes?.length > 0
      },
      showAddProductButton () {
        if (this.$store.getters.isMember) return true
        return this.$store.getters.userCanCreate &&
          this.$store.getters.claimableActorTypes?.includes('Product')
      },
      isProductButtonDisabled () {
        return this.profile?.claims?.length === 0
      },
      sidebarCommunities () {
        const list = this.joinedCommunities || []
        return this.showAllSidebarCommunities ? list : list.slice(0, 3)
      },
      analyticsStats () {
        const s = this.stats
        const fmtChange = (change) => {
          if (!change || change.direction === 'neutral' || change.percent === null) return null
          return change.isAbsolute ? '+' + change.percent + ' new' : change.percent + '%'
        }
        return [
          {
            key: 'profile-views',
            label: this.$t('personal_home_stat_profile_views', { actors: this.actorLabel }),
            value: s ? s.profileViews.value : '—',
            change: s ? fmtChange(s.profileViews.change) : null,
            trend: s ? (s.profileViews.change?.direction || 'up') : 'up',
            description: this.$t('personal_home_stat_last_30_days'),
            iconPath: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z',
          },
          {
            key: 'post-impressions',
            label: this.$t('personal_home_stat_post_impressions'),
            value: s ? s.postImpressions.value : '—',
            change: s ? fmtChange(s.postImpressions.change) : null,
            trend: s ? (s.postImpressions.change?.direction || 'up') : 'up',
            description: this.$t('personal_home_stat_across_content'),
            iconPath: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
          },
          {
            key: 'new-connections',
            label: this.$t('conversations_title'),
            value: s ? s.newConnections.value : '—',
            change: s ? fmtChange(s.newConnections.change) : null,
            trend: s ? (s.newConnections.change?.direction || 'up') : 'up',
            description: this.$t('personal_home_stat_this_month'),
            iconPath: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
          },
          {
            key: 'testimonials',
            label: this.$t('testimonials_title'),
            value: s ? s.newTestimonials.value : '—',
            change: s ? fmtChange(s.newTestimonials.change) : null,
            trend: s ? (s.newTestimonials.change?.direction || 'down') : 'down',
            description: this.$t('personal_home_stat_your_content_shared'),
            iconPath: 'M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z',
          },
        ]
      },
    },
    methods: {
      getImageForCommunity,
      toggleBioExpanded () {
        this.bioExpanded = !this.bioExpanded
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.PERSONAL_HOME, this.bioExpanded ? MATOMO_EVENT_ACTIONS.READ_MORE : MATOMO_EVENT_ACTIONS.READ_LESS, 'bio')
      },
      showManageAnnouncementModal () {
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.PERSONAL_HOME, MATOMO_EVENT_ACTIONS.CREATE_ANNOUNCEMENT)
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.MANAGE_ANNOUNCEMENT)
      },
      showAddChallengeModal () {
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.PERSONAL_HOME, MATOMO_EVENT_ACTIONS.CREATE_CHALLENGE)
        this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, { component: 'manage-challenge-panel' })
      },
      showAddProductModal () {
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.PERSONAL_HOME, MATOMO_EVENT_ACTIONS.ADD_PRODUCT)
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ADD_PRODUCT_FROM_PROFILE)
      },
      scrollToSection (id) {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      },
      async togglePersonalHome () {
        const newValue = !this.userPreferences.usePersonalHome
        this.userPreferences.usePersonalHome = newValue
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.PERSONAL_HOME, MATOMO_EVENT_ACTIONS.TOGGLE, 'use_personal_home:' + newValue)
        try {
          await updateUserPreferences({ usePersonalHome: newValue })
          this.$store.commit('USER/UPDATE_PROFILE', { usePersonalHome: newValue })
        } catch (e) {
          this.userPreferences.usePersonalHome = !newValue
        }
      },
      showUpdateProfileModal () {
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.PERSONAL_HOME, MATOMO_EVENT_ACTIONS.BUTTON_CLICK, 'update_profile')
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.EDIT_USER_PROFILE)
      },
      triggerPhotoUpload () {
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.PERSONAL_HOME, MATOMO_EVENT_ACTIONS.UPLOAD_PHOTO)
        this.$refs.photoInput.click()
      },
      async onPhotoSelected (event) {
        const file = event.target.files[0]
        if (!file) return
        this.uploadingPhoto = true
        try {
          const dataUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = e => resolve(e.target.result)
            reader.onerror = reject
            reader.readAsDataURL(file)
          })
          await updateProfile({ photo_url: dataUrl })
          const profile = await fetchProfile()
          this.$store.commit('USER/UPDATE_PROFILE', profile)
          this.$bus.emit('fetchTractionTips')
        } catch (e) { /* silently ignore */
        } finally {
          this.uploadingPhoto = false
          event.target.value = ''
        }
      },
      showCreateActorModal () {
        trackHeapEvent('clickAddActorOnUserProfile')
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.PERSONAL_HOME, MATOMO_EVENT_ACTIONS.ADD_ACTOR)
        if (this.$store.getters.isPublisherEnabled) {
          this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, { forceClaim: true })
        }
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ADD_ACTOR)
      },
      viewCommunity (community) {
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.PERSONAL_HOME, MATOMO_EVENT_ACTIONS.VIEW_COMMUNITY, community.name)
        this.$store.commit(FILTERS_MUTATION_TYPES.CLEAR)
        this.$store.commit(FILTERS_MUTATION_TYPES.SET_PORTFOLIO, community.portfolioId)
        this.$router.push('/communities/' + community.id)
      },
      viewSpottingArea (spottingArea) {
        this.$store.commit(FILTERS_MUTATION_TYPES.CLEAR)
        this.$router.push('/spotting-areas/' + spottingArea.id)
      },
      async loadUserPreferences () {
        try {
          const response = await getUserPreferences()
          this.userPreferences = response.preferences
        } catch (e) { /* silently ignore */
        }
      },
      async loadStats () {
        try {
          this.stats = await getUserStats()
        } catch (e) { /* silently ignore */
        }
      },
      loadCommunities () {
        if (!this.$store.state.communities.listData.loading) {
          this.$store.dispatch(COMMUNITY_ACTION_TYPES.FETCH_COMMUNITIES_LIST)
        }
      },
      async fetchSidebarKnowledgePosts () {
        const userId = this.profile?.id
        if (!userId) return
        try {
          const result = await fetchPostsByUser(userId, { limit: 3 })
          this.sidebarKnowledgePosts = result.data.data || []
        } catch (e) { /* silently ignore */
        }
      },
      async toggleSidebarCommunityPreference (communityId) {
        const newId = this.userPreferences.preferredCommunityId === communityId ? null : communityId
        try {
          await updateUserPreferences({ preferredCommunityId: newId })
          this.userPreferences.preferredCommunityId = newId
          this.$bus.emit('updateUserProfile')
        } catch (e) { /* silently ignore */
        }
      },
      async toggleSidebarSpottingAreaPreference (spottingAreaId) {
        const newId = this.userPreferences.preferredSpottingAreaId === spottingAreaId ? null : spottingAreaId
        try {
          await updateUserPreferences({ preferredSpottingAreaId: newId })
          this.userPreferences.preferredSpottingAreaId = newId
          this.$bus.emit('updateUserProfile')
        } catch (e) { /* silently ignore */
        }
      },
      openLeaveCommunityModal (community) {
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.PERSONAL_HOME, MATOMO_EVENT_ACTIONS.LEAVE_COMMUNITY, community.name)
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          title: this.$t('communities_leave_confirmation_title', { community: community.name }),
          body: this.$t('communities_leave_confirmation_message'),
          confirmLabel: this.$t('personal_home_relevant_yes'),
          modalBodyClasses: 'empty-fields-list',
          type: 'check-empty-fields',
          cancelLabel: this.$t('personal_home_relevant_no'),
          isCancellable: true,
          communityId: community.id,
        })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.CONFIRMATION)
      },
      async confirmLeaveCommunity () {
        const communityId = this.$store.state.ui.modalContext?.communityId
        if (!communityId) return
        try {
          await leaveCommunity(communityId)
          fetchProfile()
            .then(profile => {
              this.$store.commit('USER/UPDATE_PROFILE', profile)
            })
          this.$bus.emit('updateUserProfile')
        } catch (e) { /* silently ignore */
        }
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, null)
      },
    },
    created () {
      this.$bus.on('confirmAction', this.confirmLeaveCommunity)
      this.$bus.on('productAddedViaOverlay', (actor) => {
        const relation = [...(this.actor?.has_product ?? [])]

        relation.push({
          to: actor.id,
          to_name: actor.name,
          start: null,
          end: null,
        })

        updateActor({
          id: this.actor.id,
          data: {
            'has_product': relation,
          },
        }).then(() => {
          this.fetch(true)
        })
      })
    },
    beforeUnmount () {
      this.$bus.off('confirmAction', this.confirmLeaveCommunity)
      this.$bus.off('productAddedViaOverlay')
    },
    async mounted () {
      fetchProfile()
        .then(profile => {
          this.$store.commit('USER/UPDATE_PROFILE', profile)
        })

      if (this.$route.hash === '#notification-settings') {
        this.$router.push('/notifications?o=true')
        return
      }

      this.$store.dispatch(NOTIFICATION_ACTION_TYPES.FETCH_NOTIFICATIONS_LIST, {
        limit: this.$store.state.notifications.listData.fetchLimit,
        offset: 0,
      })

      await Promise.all([this.loadUserPreferences(), this.loadStats()])

      if (this.$store.getters.hasAccessToCommunities) {
        this.loadCommunities()
      }
    },
    mixins: [OnboardingMixin, TranslationsMixin, UiMixin, IntroJsMixin, CommunityMixin, SpottingAreaMixin],
    components: {
      Icon,
      ToggleButton,
      AvatarNew,
      PasswordAndSecurity,
      PersonalHomeContent,
      PersonalHomeCommunityCard,
      TractionTips,
      UserSocialButtons,
      DsButton,
      Avatar,
    },
  }
</script>
<style lang="scss" scoped>
  @import "../../../scss/_variables.scss";


  /* Claim name chips */
  .ph-claim-chip {
    display: inline-flex;
    align-items: center;
    padding: 0.15rem 0.55rem;
    border-radius: 100px;
    background: #f3f4f6;
    color: #374151;
    font-size: 0.72rem;
    font-weight: 500;
    white-space: nowrap;
    text-decoration: none;
    transition: background 0.15s, color 0.15s;

    &:hover {
      background: var(--primary-extra-lightest, #eff6ff);
      color: var(--primary, #2563eb);
    }

    &--more {
      background: transparent;
      color: var(--primary, #2563eb);
      border: 1px solid var(--primary-lightest, #bfdbfe);
      cursor: pointer;

      &:hover {
        background: var(--primary-extra-lightest, #eff6ff);
      }
    }
  }

  /* Primary-coloured link used for "View all" buttons */
  .ph-link-primary {
    color: var(--primary, #2563eb);

    :deep(svg) {
      flex-shrink: 0;
      transition: transform 0.2s ease;
    }

    &:hover :deep(svg) {
      transform: translateX(3px);
    }
  }

  /* ── Rail (horizontal scroll on small screens) ────────────── */
  .ph-communities-rail {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 0.75rem;

    &--suggested {
      grid-template-columns: 1fr 1fr 1fr;
    }
  }

  @media (max-width: 480px) {
    .ph-communities-rail {
      grid-template-columns: repeat(2, 1fr);
    }
  }
</style>
